import { useSyncExternalStore } from "react"
import { getSnapGuides, subscribeSnapGuides } from "../../../helpers/snapSession.helpers"

/** Figma's smart-guide red. */
const SNAP_GUIDE_COLOR = "#F24822"

/** Above the selection chrome (2600), which is hidden during a move anyway. */
const SNAP_GUIDES_Z_INDEX = 2700

/** Screen px, divided by the canvas scale so the guides look the same at any zoom. */
const LINE_WIDTH_PX = 1
const MARKER_SIZE_PX = 3

type Props = {
    canvasScaleCoef: number
    zoomScale: number
}

/**
 * The red alignment lines drawn while a selection is dragged — see snap.helpers.
 *
 * Subscribed to the guides on its own, so a snap re-renders this SVG and nothing else: the
 * drag itself never goes through React, and the canvas must not start re-rendering on
 * pointermove because of it.
 */
const SnapGuides = ({ canvasScaleCoef, zoomScale }: Props) => {
    const guides = useSyncExternalStore(subscribeSnapGuides, getSnapGuides, getSnapGuides)
    if (!guides.length) return null

    const scale = canvasScaleCoef * zoomScale || 1
    const lineWidth = LINE_WIDTH_PX / scale
    const marker = MARKER_SIZE_PX / scale

    /** a small ×, centred on the point */
    const cross = (x: number, y: number) =>
        `M${x - marker} ${y - marker}L${x + marker} ${y + marker}` +
        `M${x - marker} ${y + marker}L${x + marker} ${y - marker}`

    return (
        <svg
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 1,
                height: 1,
                overflow: "visible",
                pointerEvents: "none",
                zIndex: SNAP_GUIDES_Z_INDEX,
            }}
        >
            {guides.map((guide) => {
                const isVertical = guide.axis === "x"
                const line = isVertical
                    ? `M${guide.position} ${guide.start}L${guide.position} ${guide.end}`
                    : `M${guide.start} ${guide.position}L${guide.end} ${guide.position}`
                const markers = guide.markers
                    .map((point) =>
                        isVertical ? cross(guide.position, point) : cross(point, guide.position)
                    )
                    .join("")

                return (
                    <path
                        key={`${guide.axis}-${guide.position}`}
                        d={line + markers}
                        stroke={SNAP_GUIDE_COLOR}
                        strokeWidth={lineWidth}
                        fill="none"
                    />
                )
            })}
        </svg>
    )
}

export default SnapGuides
